import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, Switch, Tag, Typography, message } from 'antd';
import { getApnsConfig, updateApnsConfig, type AdminApnsConfig } from '../services/api';

const ApnsConfig: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasKey, setHasKey] = useState(false);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getApnsConfig();
      if (res.code !== 0) {
        message.error(res.message || '加载 APNs 配置失败');
        return;
      }
      const config = res.data as AdminApnsConfig;
      setHasKey(Boolean(config.has_key));
      form.setFieldsValue({
        team_id: config.team_id || '',
        key_id: config.key_id || '',
        bundle_id: config.bundle_id || '',
        sandbox: Boolean(config.sandbox),
        key_content: '',
      });
    } catch (error) {
      message.error('加载 APNs 配置失败');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const onFinish = async (values: {
    team_id: string;
    key_id: string;
    bundle_id: string;
    sandbox: boolean;
    key_content?: string;
  }) => {
    setSaving(true);
    try {
      const payload = {
        team_id: (values.team_id || '').trim(),
        key_id: (values.key_id || '').trim(),
        bundle_id: (values.bundle_id || '').trim(),
        sandbox: Boolean(values.sandbox),
        key_content: (values.key_content || '').trim(),
      };

      const res = await updateApnsConfig(payload);
      if (res.code !== 0) {
        message.error(res.message || '保存 APNs 配置失败');
        return;
      }

      message.success('APNs 配置已保存');
      setHasKey(Boolean(res.data?.has_key));
      form.setFieldsValue({
        team_id: res.data?.team_id || payload.team_id,
        key_id: res.data?.key_id || payload.key_id,
        bundle_id: res.data?.bundle_id || payload.bundle_id,
        sandbox: res.data?.sandbox ?? payload.sandbox,
        key_content: '',
      });
    } catch (error) {
      message.error('保存 APNs 配置失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={3} style={{ marginTop: 0 }}>
        APNs 推送配置
      </Typography.Title>

      <Card loading={loading}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="用于 iOS 后台离线消息推送。p8 私钥在 Apple 开发者后台生成，粘贴到下方即可，保存后立即生效无需重启服务。"
        />

        <div style={{ marginBottom: 16 }}>
          当前密钥状态：
          {hasKey ? <Tag color="green">已配置 p8 私钥</Tag> : <Tag color="red">未配置 p8 私钥</Tag>}
        </div>

        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            label="Team ID"
            name="team_id"
            rules={[{ required: true, message: '请输入 Team ID' }]}
            extra="Apple 开发者团队 ID，如 L624Y9V68G"
          >
            <Input placeholder="请输入 Team ID" />
          </Form.Item>

          <Form.Item
            label="Key ID"
            name="key_id"
            rules={[{ required: true, message: '请输入 Key ID' }]}
            extra="APNs Auth Key 的 Key ID"
          >
            <Input placeholder="请输入 Key ID" />
          </Form.Item>

          <Form.Item
            label="Bundle ID"
            name="bundle_id"
            rules={[{ required: true, message: '请输入 Bundle ID' }]}
            extra="应用 Bundle ID，如 com.yidaqiming.notifyapp.ios"
          >
            <Input placeholder="请输入 Bundle ID" />
          </Form.Item>

          <Form.Item
            label="沙盒环境"
            name="sandbox"
            valuePropName="checked"
            extra="开发/测试证书推送时开启沙盒，正式证书推送请关闭"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label="p8 私钥内容"
            name="key_content"
            extra="粘贴 .p8 文件的完整内容（以 -----BEGIN PRIVATE KEY----- 开头）。留空表示保留当前私钥不覆盖。"
          >
            <Input.TextArea
              rows={8}
              placeholder={'-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----'}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={saving}>
              保存配置
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ApnsConfig;
